import { Fragment } from "react";
import { useLocalization } from "@fluent/react";
import { ContentCard } from "cerulean";
import { useFeature } from "byzantine/src/dbbl/businessLogic/modules/features/hooks";
import {
  featureEquals,
  type Features,
  type FeatureValue,
} from "byzantine/src/Feature";
import styles from "./TransferTilesMenu.module.scss";
import PageLayout from "../../src/components/PageLayout";
import LongTransferTile from "./LongTransferTile";
import { useMoneyMovementMethods } from "../../src/modules/transfers";
import PaymentTile from "./PaymentTile";
import OtherItemTile from "./OtherItemTile";
import AppAuthorize from "../AppAuthorize";
import TransferPill from "./TransferPill";
import { areTemplatesAvailable } from "../Templates/constants";

const TRANSFER_FEATURE_TYPES = [
  "ach_payments",
  "wires",
  "file_import",
  "bulk_payments",
  "bill_pay",
  "loan",
];

const TRANSFER_FEATURES_WITH_SPECIAL_REDIRECT = [
  "bulk_payments",
  "file_import",
];

const OTHER_FEATURE_TYPES = ["p2p", "zelle", "naf"];

const INSTANT_FEATURES_VIA_FEDNOW = ["bulk_payments"];

const TransferTilesMenu = () => {
  const { l10n } = useLocalization();
  const { featureEnabled, features } = useFeature();

  const moneyMovementFeaturesData = useMoneyMovementMethods({
    isTilesPage: true,
  });

  if (moneyMovementFeaturesData.length === 0) {
    return null;
  }

  const fundsFeature = moneyMovementFeaturesData.find(
    (feature) => feature?.type === "funds"
  );

  const transferFeatures = TRANSFER_FEATURE_TYPES.flatMap((featureType) =>
    moneyMovementFeaturesData.filter((feature) => feature?.type === featureType)
  );

  const otherFeatures = moneyMovementFeaturesData.filter(
    (feature) =>
      typeof feature?.type === "string" &&
      OTHER_FEATURE_TYPES.includes(feature.type)
  );
  const paymentActivityEnabled = featureEnabled("payment_activity");
  const hasDualApprovalPermission = featureEnabled(
    "indigo.DualApprovalRequest_permission"
  );
  const hasRecipientPermission =
    featureEnabled("indigo.Recipient_permission") &&
    !!(
      featureEquals(
        features as unknown as Features,
        "p2p",
        "m2m" as FeatureValue
      ) ||
      features.bill_pay ||
      features.wires
    );
  const templatesEnabled = areTemplatesAvailable(features);

  // hacky way to tell if at least 2 of these options are available
  const shouldShowPillSection =
    Number(paymentActivityEnabled) +
      Number(hasDualApprovalPermission) +
      Number(hasRecipientPermission) +
      Number(templatesEnabled) >=
    2;

  const fednowEnabled = Boolean(
    features?.fednow_send && features?.expedited_payments
  );

  return (
    <PageLayout maxWidth="m">
      <div style={{ gap: 0 }}>
        <h1>
          {l10n.getString(
            "transfer-tiles-menu-header",
            null,
            "Pay and transfer"
          )}
        </h1>
        <p className={styles.subheader}>
          {l10n.getString("transfer-tiles-menu-subheader")}
        </p>
      </div>
      {shouldShowPillSection && (
        <div
          style={{
            display: "flex",
            gap: "12px",
            alignItems: "center",
            flexDirection: "row",
            flexWrap: "nowrap",
            width: "100%",
            minWidth: 0,
            maxWidth: "100%",
            overflowX: "auto",
            marginTop: "20px",
          }}
        >
          {paymentActivityEnabled && (
            <TransferPill
              label={l10n.getString("transfer-tiles-payment-activity")}
              redirect="/payment_activity"
            ></TransferPill>
          )}
          {hasDualApprovalPermission && (
            <TransferPill
              label={l10n.getString("transfer-tiles-pending-approvals")}
              redirect="/approvals/pending"
            ></TransferPill>
          )}
          {hasRecipientPermission && (
            <TransferPill
              label={l10n.getString("transfer-tiles-recipients")}
              redirect="/settings/recipients"
            ></TransferPill>
          )}
          {templatesEnabled && (
            <TransferPill
              label={l10n.getString("transfer-tiles-manage-templates")}
              redirect="/templates"
            ></TransferPill>
          )}
        </div>
      )}
      {Boolean(fundsFeature) && (
        <div>
          <div className={styles.dividerRow}>
            <p>{l10n.getString("transfer-tiles-menu-me-to-me-label")}</p>
            <div className={styles.dividerLine} />
          </div>
          <LongTransferTile
            redirect={fundsFeature?.data?.route || ""}
            icon="narmi-icon-transfer-arrows"
            header={l10n.getString(fundsFeature?.data?.translationTitle || "")}
            subHeader={l10n.getString(
              fundsFeature?.data?.translationDescription || ""
            )}
            showInstantBadge={fednowEnabled}
          />
        </div>
      )}
      {transferFeatures.length > 0 && (
        <div>
          <div className={styles.dividerRow}>
            <p>{l10n.getString("transfer-tiles-payments-label")}</p>
            <div className={styles.dividerLine} />
          </div>
          <div className={styles.tileGrid}>
            {transferFeatures.map((feature) => (
              <PaymentTile
                key={feature?.type}
                redirect={feature?.data?.route || ""}
                icon={`narmi-icon-${feature?.data?.icon}`}
                header={l10n.getString(feature.data?.translationTitle || "")}
                subHeader={l10n.getString(
                  feature.data?.translationDescription || ""
                )}
                showInstantBadge={
                  (INSTANT_FEATURES_VIA_FEDNOW.includes(feature?.type) ||
                    (feature?.type === "file_import" &&
                      featureEnabled("ach_file_import"))) &&
                  fednowEnabled
                }
                onClick={
                  TRANSFER_FEATURES_WITH_SPECIAL_REDIRECT.includes(
                    feature?.type
                  )
                    ? () => window.location.assign(feature?.data?.route || "")
                    : undefined
                }
              />
            ))}
          </div>
        </div>
      )}
      {otherFeatures.length > 0 && (
        <div>
          <div className={styles.dividerRow}>
            <p>{l10n.getString("transfer-tiles-other-label")}</p>
            <div className={styles.dividerLine} />
          </div>
          <ContentCard kind="bordered" paddingSize="none" radiusSize="m">
            {otherFeatures.map((feature, idx) => (
              <Fragment
                key={
                  feature?.type === "naf"
                    ? `naf-${feature?.nafData?.client_id || idx}`
                    : feature?.type || "other"
                }
              >
                {feature?.type === "naf" ? (
                  <AppAuthorize
                    app={feature?.nafData}
                    renderButton={(handleClick) => (
                      <OtherItemTile
                        redirect={`/apps/${feature?.nafData?.client_id || ""}`}
                        icon={`narmi-icon-${feature.nafData?.image}`}
                        header={l10n.getString(feature.nafData?.name || "")}
                        subHeader={l10n.getString(
                          feature.nafData?.description || ""
                        )}
                        onClick={handleClick}
                      />
                    )}
                  />
                ) : (
                  <OtherItemTile
                    redirect={feature?.data?.route || ""}
                    icon={
                      feature?.type === "zelle"
                        ? "narmi-icon-zelle-outline"
                        : `narmi-icon-${feature?.data?.icon}`
                    }
                    header={l10n.getString(
                      feature.data?.translationTitle ||
                        (feature?.type === "zelle" ? "Zelle" : "")
                    )}
                    subHeader={l10n.getString(
                      feature.data?.translationDescription || ""
                    )}
                  />
                )}
                {idx < otherFeatures.length - 1 && (
                  <div
                    className={`${styles.dividerLine} ${styles.dividerLineMargin}`}
                  />
                )}
              </Fragment>
            ))}
          </ContentCard>
        </div>
      )}
    </PageLayout>
  );
};

export default TransferTilesMenu;
